import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonBase,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Stack,
} from '@mui/material';
import { SystemStyleObject, Theme } from '@mui/system';

import { ReactComponent as CloudUploadIcon } from '@/assets/icons/cloud-upload-icon.svg';
import { ReactComponent as LinkExternalIcon } from '@/assets/icons/link-external.svg';

export interface DriveFolderMenuAction {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
}

interface Props {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** A safe Drive link; without one the "Open in Drive" tile is left out. */
  link?: string;
  onUpload?: () => void;
  uploading?: boolean;
  actions?: DriveFolderMenuAction[];
}

const paperSx: SystemStyleObject<Theme> = {
  minWidth: 230,
  borderRadius: '12px',
  boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.15)',
};

const tileSx: SystemStyleObject<Theme> = {
  flex: '1 0 0',
  minWidth: 0,
  flexDirection: 'column',
  gap: '4px',
  p: '8px',
  border: 1,
  borderColor: 'grey.100',
  borderRadius: '12px',
  color: 'grey.900',
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: 1.25,
  textAlign: 'center',
  '& svg': { color: 'accent.main' },
  '&:hover': { borderColor: 'accent.main' },
};

const itemSx: SystemStyleObject<Theme> = {
  gap: '5px',
  px: '8px',
  py: '4px',
  minHeight: 0,
  borderRadius: '8px',
  '& .MuiListItemIcon-root': { minWidth: 0, color: 'grey.600', fontSize: '15px' },
};

const labelProps = { fontSize: '12px', lineHeight: 1.25, color: 'grey.900' } as const;

/** A Drive folder's menu: the "Open in Drive" and "Upload" tiles over the folder's other actions. */
const DriveFolderMenu: FC<Props> = ({
  anchorEl,
  onClose,
  link,
  onUpload,
  uploading = false,
  actions = [],
}) => {
  const { t } = useTranslation();
  const hasTiles = Boolean(link) || Boolean(onUpload);

  return (
    <Popover
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: -8, horizontal: 'left' }}
      slotProps={{ paper: { sx: paperSx } }}
    >
      <Stack gap="8px" p="8px">
        {hasTiles ? (
          <Stack direction="row" gap="8px">
            {link ? (
              <ButtonBase
                component="a"
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                sx={tileSx}
              >
                <LinkExternalIcon width={22} height={22} />
                {t('artwork_drive.open_in_drive')}
              </ButtonBase>
            ) : null}
            {onUpload ? (
              <ButtonBase
                disabled={uploading}
                onClick={() => {
                  onClose();
                  onUpload();
                }}
                sx={tileSx}
              >
                <CloudUploadIcon width={22} height={22} />
                {t('artwork_drive.add_files')}
              </ButtonBase>
            ) : null}
          </Stack>
        ) : null}
        {hasTiles && actions.length ? (
          <Divider sx={{ mx: '8px', borderColor: 'grey.100' }} />
        ) : null}
        {actions.length ? (
          <MenuList dense sx={{ display: 'flex', flexDirection: 'column', gap: '8px', p: 0 }}>
            {actions.map((action) => (
              <MenuItem
                key={action.key}
                sx={itemSx}
                onClick={() => {
                  onClose();
                  action.onClick();
                }}
              >
                <ListItemIcon>{action.icon}</ListItemIcon>
                <ListItemText primaryTypographyProps={labelProps}>{action.label}</ListItemText>
              </MenuItem>
            ))}
          </MenuList>
        ) : null}
      </Stack>
    </Popover>
  );
};

export default DriveFolderMenu;
